import React, { useState, useRef } from 'react';
import { Palette, Plus, Pencil, Save, GripVertical, Search, List, Table } from 'lucide-react';
import { useNotifications } from '../../context/NotificationContext';

/**
 * StyleGuidePage - Living reference for UI patterns and components.
 * Interactive examples that demonstrate the standard patterns.
 * TODO: Delete this file before production deployment
 */

const TABS = [
  { id: 'module', label: 'Dynamic Module' },
  { id: 'notifications', label: 'Notifications' },
];

const StyleGuidePage = () => {
  const [activeTab, setActiveTab] = useState('module');
  const { notify } = useNotifications();

  return (
    <div className="dev-module">
      <div className="dev-module-header">
        <div className="dev-module-header-left">
          <Palette size={20} style={{ color: 'var(--color-primary)' }} />
          <h1>Style Guide</h1>
          <span className="module-header-meta">UI patterns & components</span>
        </div>
        <div className="dev-module-header-right">
          <div className="action-menu" ref={useRef(null)}>
            <button className="action-menu-btn" onClick={() => {}} title="Actions"><GripVertical size={16} /></button>
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div className="related-tables-header" style={{ paddingLeft: 'var(--spacing-lg)', flexShrink: 0 }}>
        <div className="related-tables-tabs">
          {TABS.map(tab => (
            <button
              key={tab.id}
              className={`related-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div className="dev-module-content-full" style={{ overflowY: 'auto', padding: 'var(--spacing-xl)' }}>

        {/* ============ DYNAMIC MODULE TAB ============ */}
        {activeTab === 'module' && (
          <>
            {/* Live labeled wireframe */}
            <div className="module-container" style={{ height: '500px' }}>
              {/* REGION A: MODULE HEADER */}
              <div className="dev-region dev-region-header">
                <div className="dev-region-label">A: MODULE HEADER (Pattern 2)</div>
                <div className="module-header">
                  <div className="module-header-left">
                    <div className="module-header-tabs">
                      <button className="tab-btn-icon active" title="List View"><List size={18} /></button>
                      <button className="tab-btn-icon" title="Datasheet View"><Table size={18} /></button>
                    </div>
                    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)' }}>5 of 100 records</span>
                  </div>
                  <div className="module-header-actions">
                    <div className="module-header-crud">
                      <button className="crud-btn" title="Add new"><Plus size={18} /></button>
                      <button className="crud-btn disabled" disabled title="Edit"><Pencil size={18} /></button>
                      <button className="crud-btn disabled" disabled title="Save"><Save size={18} /></button>
                    </div>
                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                      <Search size={14} style={{ position: 'absolute', left: '8px', color: 'var(--color-gray-400)', pointerEvents: 'none' }} />
                      <input className="form-input" placeholder="Search..." readOnly style={{ paddingLeft: '28px', width: '160px', height: '32px' }} />
                    </div>
                    <div className="action-menu">
                      <button className="action-menu-btn" title="Actions"><GripVertical size={16} /></button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="list-view">
                {/* REGION B: LIST PANEL */}
                <div className="dev-region dev-region-list">
                  <div className="dev-region-label">B: LIST PANEL</div>
                  <div className="list-panel">
                    <div className="list-panel-content">
                      {[1, 2, 3, 4, 5].map(i => (
                        <div key={i} className={`list-item ${i === 1 ? 'selected' : ''}`}>
                          <div className="list-item-primary">Sample Record {i}</div>
                          <div className="list-item-secondary">Secondary info • metadata</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* REGION C: DETAIL PANEL */}
                <div className="dev-region dev-region-detail">
                  <div className="dev-region-label">C: DETAIL PANEL</div>
                  <div className="detail-panel" style={{ overflow: 'auto' }}>
                    {/* C1: Section Header */}
                    <div className="dev-subregion">
                      <div className="dev-subregion-label">C1: Section Header</div>
                      <div className="detail-section-header">General Information</div>
                    </div>

                    {/* C2: Form Grid */}
                    <div className="dev-subregion">
                      <div className="dev-subregion-label">C2: 24-Column Form Grid</div>
                      <div className="detail-grid-24">
                        <div className="form-group col-span-8">
                          <label>col-span-8 (third)</label>
                          <input type="text" value="Standard field" readOnly className="form-input" />
                        </div>
                        <div className="form-group col-span-8">
                          <label>col-span-8</label>
                          <input type="text" value="Standard field" readOnly className="form-input" />
                        </div>
                        <div className="form-group col-span-8">
                          <label>col-span-8</label>
                          <input type="text" value="Standard field" readOnly className="form-input" />
                        </div>
                        <div className="form-group col-span-12">
                          <label>col-span-12 (half)</label>
                          <input type="text" value="Half width field" readOnly className="form-input" />
                        </div>
                        <div className="form-group col-span-12">
                          <label>col-span-12</label>
                          <input type="text" value="Half width field" readOnly className="form-input" />
                        </div>
                        <div className="form-group col-span-6">
                          <label>FK Field</label>
                          <button className="form-input fk-field"><span className="fk-field-value">Related Record →</span></button>
                        </div>
                        <div className="form-group col-span-6">
                          <label>Derived</label>
                          <input type="text" value="Computed" readOnly className="form-input derived-field" />
                        </div>
                        <div className="form-group col-span-6">
                          <label>Dirty Field</label>
                          <input type="text" value="Changed!" readOnly className="form-input dirty" />
                        </div>
                        <div className="form-group col-span-6">
                          <label>Checkbox</label>
                          <div className="checkbox-field" style={{ height: '36px', display: 'flex', alignItems: 'center' }}>
                            <input type="checkbox" checked readOnly style={{ width: '1rem', height: '1rem' }} />
                            <span>Yes</span>
                          </div>
                        </div>
                        <div className="form-group col-span-24">
                          <label>col-span-24 (full width)</label>
                          <textarea value="Full width textarea field" readOnly className="form-input" rows={2} />
                        </div>
                      </div>
                    </div>

                    {/* D: Related Tables */}
                    <div className="dev-subregion" style={{ marginTop: 'var(--spacing-md)' }}>
                      <div className="dev-subregion-label">D: RELATED TABLES</div>
                      <div className="related-tables-section" style={{ height: '120px' }}>
                        <div className="detail-section-header related-section-header">
                          <span>Related Information</span>
                        </div>
                        <div className="related-tables-header">
                          <div className="related-tables-tabs">
                            <button className="related-tab active">Tab 1 <span className="related-tab-count">(3)</span></button>
                            <button className="related-tab">Tab 2</button>
                          </div>
                        </div>
                        <div style={{ padding: 'var(--spacing-sm)', color: 'var(--color-gray-400)', fontSize: 'var(--text-xs)' }}>FilterableDataGrid renders here</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="sprint-display" style={{ marginTop: 'var(--spacing-xl)' }}>
              <h3>Region Reference</h3>
            <table className="sprint-table">
              <thead><tr><th>Region</th><th>CSS Class</th><th>Description</th></tr></thead>
              <tbody>
                <tr><td><strong>A</strong></td><td><code>.module-header</code></td><td>Pattern 2 header — view toggle, record count, CRUD, search, 6-dot</td></tr>
                <tr><td><strong>B</strong></td><td><code>.list-panel</code></td><td>Collapsible list panel with search. Items show primary + secondary text.</td></tr>
                <tr><td><strong>C</strong></td><td><code>.detail-panel</code></td><td>Selected record detail with scrollable form content</td></tr>
                <tr><td><strong>C1</strong></td><td><code>.detail-section-header</code></td><td>Teal section bar separating field groups</td></tr>
                <tr><td><strong>C2</strong></td><td><code>.detail-grid-24</code></td><td>24-column CSS grid. Fields use <code>.form-group.col-span-N</code></td></tr>
                <tr><td><strong>D</strong></td><td><code>.related-tables-section</code></td><td>Related data below detail. Tabs + FilterableDataGrid. Resizable, collapsible.</td></tr>
                <tr><td><strong>E</strong></td><td><code>.datasheet-view</code></td><td>Full-width grid view with padding. Replaces B+C when datasheet is active.</td></tr>
              </tbody>
            </table>

            <div className="sprint-spacer" />

            <h3>Header Behavior (Pattern 2)</h3>
            <table className="sprint-table">
              <thead><tr><th>View</th><th>Left Side</th><th>Right Side</th></tr></thead>
              <tbody>
                <tr><td><strong>List</strong></td><td>List icon (active) → Table icon → X of Y</td><td>+ / Trash → Pencil / X → Save → Search → 6-dot (Refresh)</td></tr>
                <tr><td><strong>Datasheet</strong></td><td>List icon → Table icon (active) → X of Y</td><td>+ → Pencil / X → Save → Search → 6-dot (Refresh + Download + Modify View + Bulk Delete)</td></tr>
              </tbody>
            </table>

            <div className="sprint-spacer" />

            <h3>Detail Panel Form Fields</h3>
            <p>Fields are arranged in a 24-column grid. Common column spans:</p>
            <table className="sprint-table">
              <thead><tr><th>Span</th><th>Width</th><th>Typical Use</th></tr></thead>
              <tbody>
                <tr><td><code>col-span-4</code></td><td>~17%</td><td>Small fields (status, boolean, short code)</td></tr>
                <tr><td><code>col-span-6</code></td><td>25%</td><td>Quarter-width fields</td></tr>
                <tr><td><code>col-span-8</code></td><td>33%</td><td>Standard fields (name, email, date)</td></tr>
                <tr><td><code>col-span-12</code></td><td>50%</td><td>Half-width fields (description, address)</td></tr>
                <tr><td><code>col-span-24</code></td><td>100%</td><td>Full-width (textarea, section header, notes)</td></tr>
              </tbody>
            </table>

            <div className="sprint-spacer" />

            <h3>Field Types</h3>
            <table className="sprint-table">
              <thead><tr><th>Type</th><th>Class / Behavior</th><th>Description</th></tr></thead>
              <tbody>
                <tr><td>Text</td><td><code>.form-input</code></td><td>Standard text input, 36px height</td></tr>
                <tr><td>Readonly</td><td><code>.form-input[readonly]</code></td><td>Light gray background, not editable</td></tr>
                <tr><td>Derived</td><td><code>.form-input.derived-field</code></td><td>Computed/immutable field (gray + italic)</td></tr>
                <tr><td>Dirty</td><td><code>.form-input.dirty</code></td><td>Yellow background + gold border when changed</td></tr>
                <tr><td>Select</td><td><code>select.form-input</code></td><td>Dropdown with cursor pointer</td></tr>
                <tr><td>Textarea</td><td><code>textarea.form-input</code></td><td>Multi-line, auto height</td></tr>
                <tr><td>FK Link</td><td><code>.form-input.fk-field</code></td><td>Clickable field that opens FK modal or navigates</td></tr>
                <tr><td>Checkbox</td><td><code>.checkbox-field</code></td><td>Checkbox + label in a flex row</td></tr>
                <tr><td>Section Header</td><td><code>.detail-section-header</code></td><td>Full-width teal bar, spans col-span-24</td></tr>
                <tr><td>Spacer</td><td><code>.col-span-N</code> (empty div)</td><td>Empty grid cell for layout control</td></tr>
              </tbody>
            </table>

            <div className="sprint-spacer" />

            <h3>Related Tables</h3>
            <ul className="sprint-unordered-list">
              <li>Sits below the detail form in the detail panel</li>
              <li>Section header with collapse toggle and optional CRUD icons</li>
              <li>Tab bar for multiple related tables (e.g., Drawing Log, IP Addresses)</li>
              <li>Each tab loads a FilterableDataGrid with the related records</li>
              <li>Resizable via drag handle on the section header</li>
              <li>Double-click a row to open FK modal for that record</li>
            </ul>

            <div className="sprint-spacer" />

            <h3>Live Example</h3>
            <p>See the actual Dynamic Module in action by navigating to any data module (Buildings, Drawings, etc.).</p>
            </div>
          </>
        )}

        {/* ============ NOTIFICATIONS TAB ============ */}
        {activeTab === 'notifications' && (
          <div className="sprint-display">
            <h2>Notification System</h2>
            <p>Two channels: <strong>Header alerts</strong> (top center) for things needing attention, <strong>Footer status</strong> (bottom left) for transient acknowledgements.</p>

            <div className="sprint-spacer" />

            <table className="sprint-table">
              <thead><tr><th>Call</th><th>Channel</th><th>Auto-dismiss</th><th>Use for</th></tr></thead>
              <tbody>
                <tr><td><code>notify.error(msg)</code></td><td>Header</td><td>Never — requires X</td><td>API failures, blocking errors</td></tr>
                <tr><td><code>notify.warning(msg)</code></td><td>Header</td><td>5 seconds</td><td>Non-fatal issues, heads-up messages</td></tr>
                <tr><td><code>notify.confirm(msg, onConfirm)</code></td><td>Header</td><td>Never — Yes/No buttons</td><td>Destructive action confirmations</td></tr>
                <tr><td><code>notify.success(msg)</code></td><td>Footer</td><td>3 seconds</td><td>Save confirmations, completed actions</td></tr>
                <tr><td><code>notify.info(msg)</code></td><td>Footer</td><td>4 seconds</td><td>Background completions, quiet FYIs</td></tr>
                <tr><td><code>notify.status(msg, 0)</code></td><td>Footer</td><td>Never (persistent)</td><td>Loading / working states</td></tr>
              </tbody>
            </table>

            <div className="sprint-spacer" />
            <h3>Multi-Alert Behavior</h3>
            <p>1 alert renders normally. 2+ alerts collapse to a summary row (expanded by default) showing the worst severity icon, alert count, a Dismiss All button, and a collapse toggle. Each alert in the expanded stack retains its individual dismiss control.</p>

            <div className="sprint-spacer" />
            <h3>Live Examples</h3>
            <p>Fire these to see them in context. Stack multiples to see the collapse behavior.</p>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-sm)', marginTop: 'var(--spacing-md)' }}>
              {[
                { label: 'Error', color: 'var(--color-error)', action: () => notify.error('Something went wrong — this requires acknowledgement.') },
                { label: 'Warning', color: 'var(--color-status-warning)', action: () => notify.warning('Heads up — this will auto-dismiss in 5 seconds.') },
                { label: 'Confirm', color: 'var(--color-primary)', action: () => notify.confirm('Delete this record? This cannot be undone.', () => notify.success('Record deleted.'), { confirmText: 'Delete', cancelText: 'Cancel' }) },
                { label: 'Success (footer)', color: 'var(--color-status-success)', action: () => notify.success('Record saved successfully.') },
                { label: 'Info (footer)', color: 'var(--color-status-info)', action: () => notify.info('Background sync completed — 142 records updated.') },
                { label: 'Persistent Status', color: 'var(--color-text-secondary)', action: () => notify.status('Syncing data…', 0) },
                { label: 'Clear Status', color: 'var(--color-text-secondary)', action: () => notify.clearStatus() },
              ].map(({ label, color, action }) => (
                <button key={label} onClick={action} style={{
                  padding: '6px 14px', fontSize: 'var(--text-sm)', fontWeight: 500,
                  border: `1px solid ${color}`, borderRadius: 'var(--radius-sm)',
                  background: 'transparent', color, cursor: 'pointer', whiteSpace: 'nowrap',
                  transition: 'opacity 0.15s ease'
                }}>
                  {label}
                </button>
              ))}
            </div>

            <div className="sprint-spacer" />
            <h3>Stack Test</h3>
            <p>Fires N alerts at once — watch the first-to-stack transition and scrolling behavior.</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-sm)', marginTop: 'var(--spacing-md)' }}>
              {[3, 5, 10, 20].map(n => (
                <button key={n} onClick={() => {
                  const types = ['error', 'warning', 'error', 'warning', 'error'];
                  const msgs = [
                    'Database connection timed out.',
                    'Some fields failed validation.',
                    'Save failed — record is locked by another user.',
                    'Session will expire in 5 minutes.',
                    'Sync conflict detected on Tags table.',
                    'Permission denied on Buildings endpoint.',
                    'FK reference missing — check Organization field.',
                    'API rate limit approaching.',
                    'Draft changes not saved.',
                    'Network timeout on report export.',
                    'Schema mismatch detected.',
                    'User impersonation session active.',
                    'Stale data warning — last refresh over 10 minutes ago.',
                    'Export queue is full.',
                    'Duplicate record detected.',
                    'Backend returned 503.',
                    'Token refresh failed.',
                    'File size exceeds limit.',
                    'Required field missing.',
                    'Batch operation partially failed.',
                  ];
                  for (let i = 0; i < n; i++) {
                    const type = types[i % types.length];
                    if (type === 'error') notify.error(msgs[i % msgs.length]);
                    else notify.warning(msgs[i % msgs.length]);
                  }
                }} style={{
                  padding: '6px 14px', fontSize: 'var(--text-sm)', fontWeight: 500,
                  border: '1px solid var(--color-primary)', borderRadius: 'var(--radius-sm)',
                  background: 'var(--color-primary)', color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap'
                }}>
                  Stack {n}
                </button>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default StyleGuidePage;
